<template>
  <b-modal
    ref="forceSyncModal"
    :visible="visible"
    title="Forzar sincronización"
    @hide="handleModalHide"
    no-close-on-backdrop
    :no-close-on-esc="loading"
  >
    <b-nav tabs class="mb-3">
      <b-nav-item
        v-for="option in syncTypeOptions"
        :key="option.value"
        :active="syncType === option.value"
        @click="syncType = option.value; resetFields()"
      >
        {{ option.text }}
      </b-nav-item>
    </b-nav>

    <template v-if="syncType === 'invoices'">
      <b-form-group label="Desde:" label-for="inv-from-date">
        <b-form-datepicker
          id="inv-from-date"
          v-model="invoices.from"
          :date-format-options="dateFormatOptions"
          :max="invoices.to"
          :hide-header="true"
          :label-help="null"
          start-weekday="1"
          placeholder="Desde"
          :disabled="!!invoices.documentNo"
        />
      </b-form-group>

      <b-form-group label="Hasta:" label-for="inv-to-date">
        <b-form-datepicker
          id="inv-to-date"
          v-model="invoices.to"
          :date-format-options="dateFormatOptions"
          :min="invoices.from"
          :hide-header="true"
          :label-help="null"
          start-weekday="1"
          placeholder="Hasta"
          :disabled="!!invoices.documentNo"
        />
      </b-form-group>

      <b-form-group label="Número de documento:" label-for="inv-document-no">
        <b-form-input
          id="inv-document-no"
          v-model="invoices.documentNo"
          placeholder="Vacío"
          trim
        />
        <small class="text-muted">
          Si se introduce un número de documento, las fechas serán ignoradas.
        </small>
      </b-form-group>
    </template>

    <template v-if="syncType === 'payments'">
      <b-form-group label="Desde:" label-for="pay-from-date">
        <b-form-datepicker
          id="pay-from-date"
          v-model="payments.from"
          :date-format-options="dateFormatOptions"
          :max="payments.to"
          :hide-header="true"
          :label-help="null"
          start-weekday="1"
          placeholder="Desde"
          :disabled="!!payments.bcId"
        />
      </b-form-group>

      <b-form-group label="Hasta:" label-for="pay-to-date">
        <b-form-datepicker
          id="pay-to-date"
          v-model="payments.to"
          :date-format-options="dateFormatOptions"
          :min="payments.from"
          :hide-header="true"
          :label-help="null"
          start-weekday="1"
          placeholder="Hasta"
          :disabled="!!payments.bcId"
        />
      </b-form-group>

      <b-form-group label="Número de documento:" label-for="pay-bc-id">
        <b-form-input
          id="pay-bc-id"
          v-model="payments.bcId"
          placeholder="Vacío"
          trim
        />
        <small class="text-muted">
          Si se introduce un número de documento, las fechas serán ignoradas.
        </small>
      </b-form-group>
    </template>

    <template #modal-footer>
      <div class="w-100 d-flex justify-content-center">
        <button
          type="button"
          class="btn btn-primary d-flex align-items-center justify-content-center"
          style="min-width: 140px;"
          :disabled="isSyncDisabled"
          @click="handleSync"
        >
          <b-spinner small class="mr-2" v-if="loading" />
          {{ loading ? 'Sincronizando...' : 'Sincronizar' }}
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import Vue from 'vue'

export default {
  name: 'ForceSyncBCModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      syncType: 'invoices',
      syncTypeOptions: [
        { text: 'Facturas', value: 'invoices' },
        { text: 'Pagos', value: 'payments' },
      ],
      dateFormatOptions: {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      },
      invoices: {
        from: this.getYesterday(),
        to: this.getYesterday(),
        documentNo: '',
      },
      payments: {
        from: this.getYesterday(),
        to: this.getYesterday(),
        bcId: '',
      },
      loading: false,
    }
  },
  computed: {
    isSyncDisabled() {
      if (this.loading) return true
      if (this.syncType === 'invoices') {
        return !this.invoices.documentNo && (!this.invoices.from || !this.invoices.to)
      }
      // pagos: se necesita al menos bcId o el par from+to
      return !this.payments.bcId && (!this.payments.from || !this.payments.to)
    },
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.syncType = 'invoices'
        this.resetFields()
      }
    },
  },
  methods: {
    getYesterday() {
      const date = new Date()
      date.setDate(date.getDate() - 1)
      return date.toISOString().slice(0, 10)
    },
    resetFields() {
      this.invoices = { from: this.getYesterday(), to: this.getYesterday(), documentNo: '' }
      this.payments = { from: this.getYesterday(), to: this.getYesterday(), bcId: '' }
    },
    async handleSync() {
      this.loading = true
      try {
        if (this.syncType === 'invoices') {
          const invoicesBody = this.invoices.documentNo
            ? { document_no: this.invoices.documentNo }
            : { from: this.invoices.from, to: this.invoices.to }

          await Vue.prototype.$http.post('/purchase-invoices/force-sync-bc', invoicesBody)
        } else {
          const body = this.payments.bcId
            ? { bc_id: this.payments.bcId }
            : { from: this.payments.from, to: this.payments.to }

          await Vue.prototype.$http.post('/purchase-invoice-payments/force-sync-bc', body)
        }

        this.$toast.success('Sincronización iniciada correctamente')
        this.$emit('sync-success')
        this.loading = false
        this.closeModal()
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Error al iniciar la sincronización. Por favor inténtelo de nuevo.'
        this.$toast.error(errorMessage)
      } finally {
        this.loading = false
      }
    },
    closeModal() {
      this.$refs.forceSyncModal.hide()
    },
    handleModalHide() {
      if (!this.loading) {
        this.$emit('close')
      }
    },
  },
}
</script>
